<template>
  <div class="stats-card" :class="[variantClass, { compact }]">
    <div class="stat-icon">
      <i :class="iconClass"></i>
    </div>
    <div class="stat-content">
      <div class="stat-value">{{ displayValue }}</div>
      <div class="stat-label">
        {{ label }}
        <InfoTip :explanation="explanation" :label="label" :test-id="explanationTestId" />
      </div>
    </div>
    <div class="stat-badge" v-if="showBadge">
      <i :class="badgeIcon"></i>
    </div>
    <!-- Indicador colorido no topo -->
    <div class="stat-indicator"></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { ExplanationInput } from '../explanations/types';
import InfoTip from './InfoTip.vue';

const props = withDefaults(defineProps<{
  value: number | string;
  label: string;
  icon?: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  badgeIcon?: string;
  compact?: boolean;
  /**
   * How the number is reached (The Self-Explaining Rule: a highlight number
   * explains itself) — an ⓘ next to the label. Usually a catalog entry with
   * `calculation` and `scope`.
   */
  explanation?: ExplanationInput;
  /** data-testid of that ⓘ. */
  explanationTestId?: string;
}>(), {
  icon: 'pi pi-chart-bar',
  variant: 'primary',
  badgeIcon: '',
  compact: false,
  explanation: null,
  explanationTestId: '',
});

const displayValue = computed((): number | string => {
  return props.value;
});

const variantClass = computed((): string => {
  return `variant-${props.variant}`;
});

const iconClass = computed((): string => {
  return props.icon;
});

const showBadge = computed((): boolean => {
  return !!props.badgeIcon;
});
</script>

<style scoped>
.stats-card {
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

/* Indicador colorido no topo */
.stat-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--p-surface-border);
  transition: background 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

/* Variantes de cor */
.variant-primary .stat-indicator {
  background: linear-gradient(90deg, var(--p-primary-400), var(--p-primary-600));
}

.variant-success .stat-indicator {
  background: linear-gradient(90deg, #22c55e, #16a34a);
}

.variant-warning .stat-indicator {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.variant-danger .stat-indicator {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.variant-info .stat-indicator {
  background: linear-gradient(90deg, #3b82f6, #2563eb);
}

.stat-icon {
  width: 56px;
  height: 56px;
  background: var(--p-surface-100);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon i {
  font-size: 26px;
  color: var(--p-text-color-secondary);
}

/* Background do ícone por variante */
.variant-primary .stat-icon {
  background: var(--p-primary-50);
}
.variant-primary .stat-icon i {
  color: var(--p-primary-600);
}

.variant-success .stat-icon {
  background: #dcfce7;
}
.variant-success .stat-icon i {
  color: #16a34a;
}

.variant-warning .stat-icon {
  background: #ffedd5;
}
.variant-warning .stat-icon i {
  color: #d97706;
}

.variant-danger .stat-icon {
  background: #fee2e2;
}
.variant-danger .stat-icon i {
  color: #dc2626;
}

.variant-info .stat-icon {
  background: #dbeafe;
}
.variant-info .stat-icon i {
  color: #2563eb;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
  color: var(--p-text-color);
}

.stat-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--p-text-color-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
}

/* Badge color por variante */
.variant-primary .stat-badge {
  background: var(--p-primary-50);
}
.variant-primary .stat-badge i {
  color: var(--p-primary-600);
}

.variant-success .stat-badge {
  background: #dcfce7;
}
.variant-success .stat-badge i {
  color: #16a34a;
}

.variant-warning .stat-badge {
  background: #ffedd5;
}
.variant-warning .stat-badge i {
  color: #d97706;
}

.variant-danger .stat-badge {
  background: #fee2e2;
}
.variant-danger .stat-badge i {
  color: #dc2626;
}

.variant-info .stat-badge {
  background: #dbeafe;
}
.variant-info .stat-badge i {
  color: #2563eb;
}

/* Compact variant */
.compact {
  padding: 12px 14px;
  gap: 10px;
}
.compact .stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
}
.compact .stat-icon i {
  font-size: 16px;
}
.compact .stat-value {
  font-size: 20px;
  margin-bottom: 2px;
}
.compact .stat-label {
  font-size: 11px;
}
.compact .stat-badge {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}
.compact .stat-badge i {
  font-size: 11px;
}

/* Responsive */
@media (max-width: 768px) {
  .stats-card {
    padding: 16px;
  }

  .stat-value {
    font-size: 26px;
  }
}
</style>
